// src/api/admin/brandingAdminApi.ts
/**
 * @file The clinic's ADMIN branding read/write (S9, ClickUp 86d3w1g8u).
 * @module api/admin/brandingAdminApi
 *
 * The write half of the whitelabel feature, talking to S4's four endpoints on the clinic's
 * own auth service. Not to be confused with `api/branding/brandingApi.ts` — that one is the
 * PUBLIC, unauthenticated read the whole app renders from (decision D1). This one is
 * authenticated, administrator-only, and read by exactly one screen.
 *
 * *** THE SERVER IS THE GATE, NOT THIS MODULE. ***
 *
 * Every write here 403s with `{"message":"branding.not_entitled"}` at a clinic without the
 * plan, and 403s with `{"message":"Forbidden"}` for a non-administrator — both enforced in
 * S4's controller. The settings tab's `visible()` and the read-only state this module's
 * `entitled` flag drives are PRESENTATION. Never treat either as authorisation.
 */

import { ENV } from '../../config/runtimeEnv';
import { http } from '../../lib/http/client';

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

/** The three image slots. `logo_dark` is optional (D13) and falls back to `logo`. */
export type BrandingAssetVariant = 'logo' | 'logo_dark' | 'favicon';

export interface BrandingAsset {
	/** The file service's uuid for the stored asset, or `null` when the slot is empty. */
	file_id: string | null;
	/** The composed public URL, or `null` when the slot is empty. */
	url: string | null;
}

/**
 * S4's response shape. `show`, `update` and `uploadAsset` all return this same object, so
 * one response can replace the whole of local state regardless of which call produced it.
 */
export interface BrandingAdmin {
	/**
	 * Does the clinic hold the whitelabel plan RIGHT NOW?
	 *
	 * ⚠️ The read is deliberately NOT entitlement-gated (S4), so this can be `false` while
	 * the other fields are populated: a clinic whose plan was removed keeps its branding
	 * stored indefinitely (D9). That state renders read-only with a retention message, not
	 * as an empty form.
	 */
	entitled: boolean;
	display_name: string | null;
	primary_color: string | null;
	logo: BrandingAsset;
	logo_dark: BrandingAsset;
	favicon: BrandingAsset;
}

/**
 * ⚠️ AN ABSENT KEY IS LEFT UNCHANGED; AN EXPLICIT `null` CLEARS.
 *
 * That is S4's contract, and it is why this interface uses optional-AND-nullable rather
 * than plain nullable: `{}` is "change nothing", `{ brand_primary_color: null }` is "clear
 * the colour and leave the name alone". Saving one field can never wipe the other.
 */
export interface UpdateBrandingPayload {
	brand_display_name?: string | null;
	brand_primary_color?: string | null;
}

const BRANDING_PATH = `${API_BASE_URL}/admin/company/branding`;

/** `""` is an empty slot, not a URL — same rule as `normalizeBranding` in S6. */
const str = (value: unknown): string | null =>
	typeof value === 'string' && value.trim() !== '' ? value : null;

const asset = (value: unknown): BrandingAsset => {
	if (value === null || typeof value !== 'object' || Array.isArray(value)) {
		return { file_id: null, url: null };
	}
	const raw = value as Record<string, unknown>;
	return { file_id: str(raw.file_id), url: str(raw.url) };
};

/**
 * Merge an untrusted response over an empty record.
 *
 * Same reasoning as S6's `normalizeBranding`: a proxy error page, a partial body or a
 * future backend change must not be able to hand the settings screen an object without a
 * `logo` key and produce `undefined.url` inside a render. `entitled` fails CLOSED — a
 * non-boolean means not entitled, so an unreadable response renders the read-only state
 * rather than a form whose every write would 403.
 */
export const normalizeBrandingAdmin = (payload: unknown): BrandingAdmin => {
	const raw =
		payload !== null && typeof payload === 'object' && !Array.isArray(payload)
			? (payload as Record<string, unknown>)
			: {};

	return {
		entitled: raw.entitled === true,
		display_name: str(raw.display_name),
		primary_color: str(raw.primary_color),
		logo: asset(raw.logo),
		logo_dark: asset(raw.logo_dark),
		favicon: asset(raw.favicon),
	};
};

/** GET — works even when the clinic is NOT entitled. See {@link BrandingAdmin.entitled}. */
export const fetchBrandingAdmin = async (): Promise<BrandingAdmin> => {
	const response = await http.get(BRANDING_PATH);
	return normalizeBrandingAdmin(response.data);
};

/** PUT — see {@link UpdateBrandingPayload} for the absent-vs-null rule. */
export const updateBranding = async (payload: UpdateBrandingPayload): Promise<BrandingAdmin> => {
	const response = await http.put(BRANDING_PATH, payload);
	return normalizeBrandingAdmin(response.data);
};

/**
 * POST multipart `file` + `variant`.
 *
 * ⚠️ No explicit `Content-Type`: the browser must set the multipart boundary itself, and
 * naming the header strips it. Same as `uploadClinicDocument`.
 */
export const uploadBrandingAsset = async (
	variant: BrandingAssetVariant,
	file: File,
): Promise<BrandingAdmin> => {
	const form = new FormData();
	form.append('file', file);
	form.append('variant', variant);

	const response = await http.post(`${BRANDING_PATH}/asset`, form);
	return normalizeBrandingAdmin(response.data);
};

/**
 * DELETE — idempotent, 204 with NO BODY even when the slot is already empty.
 *
 * ⚠️ Returns `void`, not `BrandingAdmin`, precisely because of that: the caller refetches
 * rather than being handed a body this endpoint does not send.
 */
export const deleteBrandingAsset = async (variant: BrandingAssetVariant): Promise<void> => {
	await http.delete(`${BRANDING_PATH}/asset`, {
		data: { variant },
		params: { variant },
	});
};
